<template>
  <div id="stock-transfer-create" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue darken-2"
      v-if="loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-snackbar v-model="successSnackbar" color="success">
      Transfer submitted successfully!
      <template v-slot:action="{}">
        <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
    <v-form ref="transferForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock :headerText="headerText"></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Out Branch</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="selectedBranchOut"
                    @change="fetchStores('out')"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                    placeholder="Select Out Branch"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Out Store</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="selectedStoreOut"
                    @change="fetchProducts()"
                    :items="storesOut"
                    item-text="store_name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                    placeholder="Select Out Store"
                  ></v-autocomplete>
                </v-col>
              </v-col>
            </v-row>
            <p class="text-h7 font-weight-bold align-center">To</p>
            <v-row>
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">In Branch</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="selectedBranchIn"
                    @change="fetchStores('in')"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                    placeholder="Select In Branch"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">In Store</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="selectedStoreIn"
                    :items="storesIn"
                    item-text="store_name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                    placeholder="Select In Store"
                  ></v-autocomplete>
                </v-col>
              </v-col>
            </v-row>

            <v-col cols="12">
              <ItemsTable
                :items="formData.items"
                :products="products"
                @add-item="addItem"
                @remove-item="removeItem"
              />
            </v-col>

            <v-row class="mt-4">
              <v-col cols="12" md="2">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Transfer Status</p>
                </v-col>
                <v-select
                  v-model="formData.status"
                  :items="statuses"
                  placeholder="Select Status"
                  dense
                  outlined
                  :rules="[rules.required]"
                ></v-select>
              </v-col>
            </v-row>

            <v-col cols="12" md="12" sm="12">
              <v-card-actions class="mt-10">
                <v-row>
                  <v-col cols="12" md="10" xs="6"></v-col>
                  <v-col cols="12" md="2" xs="6">
                    <v-btn
                      v-if="hasPermission('stock_transfer.create')"
                      large
                      color="#00B3FF"
                      dark
                      depressed
                      block
                      :disabled="loadingSubmit"
                      @click="validateForm"
                    >
                      <v-progress-circular
                        v-if="loadingSubmit"
                        indeterminate
                        size="24"
                        color="white"
                      ></v-progress-circular>
                      <span v-else>Transfer</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-col>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import ItemsTable from "@/components/stockTransfer/ItemsTable.vue";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import Alert from "@/components/shared/Alert";
import { StockTransferService } from "@/services/stockTransfer-service.js";
import { checkError } from "@/config/errors.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "StockTransferCreateForm",
  components: { ItemsTable, HeaderDividerBlock, Alert },
  props: {
    formData: { type: Object, required: true },
    proploading: { type: Boolean, required: true },
  },
  data() {
    return {
      loading: this.proploading,
      valid: false,
      branches: [],
      storesOut: [],
      storesIn: [],
      selectedBranchOut: null,
      selectedStoreOut: null,
      selectedBranchIn: null,
      selectedStoreIn: null,
      products: [],
      userId: localStorage.getItem("userId"),
      rules: {
        required: (value) => !!value || "This field is required.",
      },
      errors: null,
      statuses: ["Pending", "Completed"],
      storeLoadingOut: false,
      storeLoadingIn: false,
      productLoading: false,
      successSnackbar: false,
    };
  },
  methods: {
    addItem() {
      this.formData.items.push({
        product_id: "",
        batch_id: "",
        quantity: 0,
      });
    },
    removeItem(index) {
      this.formData.items.splice(index, 1);
    },

    resetForm() {
      this.formData = {
        supplier_code: "",
        branch_code: "",
        items: [],
        comment: "",
        expected_delivery_date: "",
      };
    },

    validateForm() {
      const isValid = this.$refs.transferForm.validate();
      if (isValid) {
        this.submitForm();
      }
    },

    async submitForm() {
      this.formData.outBranch = this.selectedBranchOut;
      this.formData.outStore = this.selectedStoreOut;
      this.formData.inBranch = this.selectedBranchIn;
      this.formData.inStore = this.selectedStoreIn;
      this.formData.userId = this.userId;
      this.formData.transfer = "Stock";

      this.loading = true;
      try {
        await StockTransferService.createTransfer(this.formData);
        this.successSnackbar = true;
        this.$router.push({ name: "StockTransfers" });
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },

    async fetchBranches() {
      try {
        const branchesResponse = await StockTransferService.getBranches();
        this.branches = branchesResponse.data;
      } catch (error) {
        console.error("Error fetching data:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        console.log("Branches : ", this.branches);
      }
    },
    async fetchStores(type) {
      if (type === "out") {
        this.storesOut = [];
        this.selectedStoreIn = null;
        this.storeLoadingOut = true;
      } else {
        this.storeLoadingIn = true;
      }
      try {
        const branchId =
          type === "out" ? this.selectedBranchOut : this.selectedBranchIn;
        const storesResponse = await StockTransferService.getStores(branchId);
        const filteredStores = storesResponse.data.filter((store) => {
          if (type === "out") {
            return store.id !== this.selectedStoreIn;
          } else {
            return store.id !== this.selectedStoreOut;
          }
        });

        console.log(filteredStores);
        if (type === "out") {
          this.storesOut = filteredStores;
          this.products = [];
        } else {
          this.storesIn = filteredStores;
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        if (type === "out") {
          this.storeLoadingOut = false;
        } else {
          this.storeLoadingIn = false;
        }
      }
    },
    async fetchProducts() {
      this.productLoading = true;
      try {
        const productResponse = await StockTransferService.getProducts(
          this.selectedBranchOut,
          this.selectedStoreOut
        );

        this.products = productResponse.data;
        this.storesIn = [];
        this.selectedBranchIn = null;
      } catch (error) {
        console.error("Error fetching products:", error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.productLoading = false;
        console.log("Products : ", this.products);
      }
    },
    onDismissed() {
      this.errors = null;
    },
  },

  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    headerText() {
      return "Create Stock Transfer";
    },
  },

  async created() {
    this.fetchBranches();
    this.formData.status = null;
  },

  watch: {
    proploading(newVal) {
      this.loading = newVal;
    },
  },
};
</script>
